import { MobileScoreCard } from '@/components/mobile/mobile-score-card'
import { QuickReplyChips } from '@/components/mobile/quick-reply-chips'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/hooks/use-auth'
import { useChats, useCreateChat, useInfiniteMessages } from '@/hooks/use-chat'
import { useChatStreaming } from '@/hooks/use-chat-streaming'
import { useDocumentProcessingProgress } from '@/hooks/use-document-processing-progress'
import { useCurrentResume, useDocuments } from '@/hooks/use-documents'
import { useSSEEvent } from '@/hooks/use-event-stream'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { cn } from '@/lib/utils'
import { documentService } from '@/services/document.service'
import { externalJobsService } from '@/services/external-jobs.service'
import { useChatStore } from '@/stores/chat-store'
import type { ChatMessage } from '@/stores/chat-store'
import type { QuickAction } from '@/types'
import { useQueryClient } from '@tanstack/react-query'
import { ArrowDown, Loader, MessageCircle, Sparkles } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  formatMessageTime,
  getMessageDisplayType,
  groupMessagesByDate,
  parseJobContext,
  parseMatchData,
} from './chat-helpers'
import { ChatInput } from './chat-input'
import { ConversationFeedback } from './conversation-feedback'
import { MessageFeedback } from './message-feedback'
import { InlineJobChip, InlineMatchCard, MESSAGE_RENDERERS } from './message-renderers'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000'

interface ChatPanelProps {
  /** When true, renders side panels (resume/learning) in a provided slot instead of inline */
  className?: string
}

export function ChatPanel({ className }: ChatPanelProps) {
  const { user } = useAuth()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { sendMessageStreaming, streamingIdRef, userScrolledUpRef } = useChatStreaming()
  const { currentResume } = useCurrentResume()

  const {
    chatId,
    messages,
    isSending,
    setChatId,
    setMessages,
    userDocumentId,
    setPreviewPanelOpen,
    setResumeContent,
    setOriginalResumeContent,
    setUserDocumentId,
    scrollPosition: savedScrollPosition,
    setScrollPosition,
  } = useChatStore()

  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isMatchingJobs, setIsMatchingJobs] = useState(false)
  const [supportMode, setSupportMode] = useState(false)

  const { data: allDocuments } = useDocuments()
  const { processingItems } = useDocumentProcessingProgress(allDocuments)
  const [learningStatusText, setLearningStatusText] = useState<string | null>(null)

  // Listen for learning progress status via SSE
  useSSEEvent('learning_profile_status_changed', (event) => {
    const data = event.data as { status?: string; status_text?: string }
    if (data.status_text) {
      setLearningStatusText(data.status_text)
    } else if (data.status === 'completed' || data.status === 'failed') {
      setLearningStatusText(null)
    }
  })
  useSSEEvent('learning_topic_status_changed', (event) => {
    const data = event.data as { status?: string; status_text?: string }
    if (data.status_text) {
      setLearningStatusText(data.status_text)
    } else if (data.status === 'completed' || data.status === 'failed') {
      setLearningStatusText(null)
    }
  })
  useSSEEvent('job_matching_status_changed', () => {
    if (!chatId) return
    void refetchMessages()
    queryClient.invalidateQueries({ queryKey: ['chats', chatId, 'messages', 'infinite'] })
  })

  const activeStatusText =
    processingItems.find((p) => p.statusText && p.status === 'processing')?.statusText ||
    learningStatusText

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const messagesContainerRef = useRef<HTMLDivElement>(null)
  const greetingInitiatedRef = useRef(false)
  const isInitialLoadRef = useRef(true)
  const shouldScrollToBottomRef = useRef(false)
  const previousScrollHeightRef = useRef<number>(0)
  const chatCreationInitiatedRef = useRef(false)
  const fetchingMoreRef = useRef(false)
  const shouldPreservePrependScrollRef = useRef(false)
  const savedScrollPositionRef = useRef(savedScrollPosition)
  const sentPendingCoachMessageRef = useRef(false)
  const typingAutoFollowRef = useRef(false)
  const firstCoachBubbleAutoScrolledRef = useRef(false)

  useEffect(() => {
    shouldScrollToBottomRef.current = true
  }, [])

  useEffect(() => {
    if (isSending) {
      typingAutoFollowRef.current = true
      firstCoachBubbleAutoScrolledRef.current = false
      userScrolledUpRef.current = false
    }
  }, [isSending, userScrolledUpRef])

  useEffect(() => {
    if (!isSending) {
      typingAutoFollowRef.current = false
    }
  }, [isSending])

  useEffect(() => {
    if (!chatId || isSending || sentPendingCoachMessageRef.current) return

    const pendingRaw = sessionStorage.getItem('pending_coach_message')
    if (!pendingRaw) return

    try {
      const pending = JSON.parse(pendingRaw) as { message?: string; documentId?: number }
      if (pending.message) {
        sentPendingCoachMessageRef.current = true
        sessionStorage.removeItem('pending_coach_message')
        sendMessageStreaming(
          pending.message,
          typeof pending.documentId === 'number' ? [pending.documentId] : undefined,
          undefined,
          undefined,
        )
      }
    } catch {
      sessionStorage.removeItem('pending_coach_message')
    }
  }, [chatId, isSending, sendMessageStreaming])

  const { data: chats, isLoading: chatsLoading } = useChats()
  const createChatMutation = useCreateChat()
  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch: refetchMessages,
  } = useInfiniteMessages(chatId || '')

  const fetchedMessages = useMemo(() => {
    if (!infiniteData?.pages) return undefined
    const allPages = [...infiniteData.pages].reverse()
    return allPages.flat()
  }, [infiniteData])

  // Get or create the user's chat
  useEffect(() => {
    if (chatsLoading) return

    if (chats && chats.length > 0) {
      const chat = chats[0]
      if (chat && chat.id !== chatId) {
        setChatId(chat.id)
      }
    } else if (!chatCreationInitiatedRef.current) {
      chatCreationInitiatedRef.current = true
      createChatMutation.mutate('My Conversation', {
        onSuccess: (newChat) => {
          setChatId(newChat.id)
        },
      })
    }
  }, [chats, chatsLoading, chatId, createChatMutation, setChatId])

  // Load document detail when document ID is set
  useEffect(() => {
    if (!userDocumentId) return
    setPreviewPanelOpen(true)

    const loadDocumentDetail = async () => {
      try {
        const detail = await documentService.getDocument(userDocumentId)
        if (detail.parsed_resume_data) {
          setOriginalResumeContent(detail.parsed_resume_data)
          if (!detail.draft_content_json) {
            setResumeContent(detail.parsed_resume_data)
          }
        }
        if (detail.draft_content_json) {
          setResumeContent(detail.draft_content_json)
        }
      } catch (error) {
        console.error('Failed to load document detail:', error)
      }
    }
    loadDocumentDetail()
  }, [userDocumentId, setPreviewPanelOpen, setOriginalResumeContent, setResumeContent])

  useEffect(() => {
    if (!userDocumentId || !chatId) return
    queryClient.invalidateQueries({ queryKey: ['chats', chatId, 'messages', 'infinite'] })
    void refetchMessages()
  }, [userDocumentId, chatId, queryClient, refetchMessages])

  // Load messages on initial page load only. After that, local state is the
  // source of truth - new messages come from streaming and SSE events, not refetches.
  useEffect(() => {
    if (fetchedMessages && !isSending) {
      const container = messagesContainerRef.current
      const prevScrollHeight = previousScrollHeightRef.current

      const nonEmptyMessages = fetchedMessages.filter((m) => m.content?.trim())
      setMessages((prev) => {
        const parseTime = (value: string | Date | undefined) => {
          const t = new Date(value ?? '').getTime()
          return Number.isFinite(t) ? t : 0
        }
        const existingIds = new Set(prev.map((m) => String(m.id)))
        const latestLocalTimestamp = prev.reduce((max, m) => {
          const ts = parseTime(m.created_at)
          return ts > max ? ts : max
        }, 0)
        const incomingNewerMessages = nonEmptyMessages.filter((m) => {
          const messageId = String(m.id)
          if (existingIds.has(messageId)) return false
          const serverTs = parseTime(m.created_at)
          return latestLocalTimestamp === 0 || serverTs >= latestLocalTimestamp
        })

        if (prev.length === 0) return nonEmptyMessages
        if (incomingNewerMessages.length > 0) return [...prev, ...incomingNewerMessages]
        return prev
      })

      if (isInitialLoadRef.current || shouldScrollToBottomRef.current) {
        const wasInitial = isInitialLoadRef.current
        isInitialLoadRef.current = false
        shouldScrollToBottomRef.current = false
        shouldPreservePrependScrollRef.current = false
        previousScrollHeightRef.current = 0
        setTimeout(() => {
          if (messagesContainerRef.current) {
            // Restore saved scroll position on remount (tab switch), otherwise scroll to bottom
            if (wasInitial && savedScrollPositionRef.current > 0) {
              messagesContainerRef.current.scrollTop = savedScrollPositionRef.current
            } else {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
            }
          }
        }, 50)
      } else if (container && shouldPreservePrependScrollRef.current && prevScrollHeight > 0) {
        requestAnimationFrame(() => {
          if (messagesContainerRef.current) {
            const newScrollHeight = messagesContainerRef.current.scrollHeight
            const scrollDiff = newScrollHeight - prevScrollHeight
            messagesContainerRef.current.scrollTop = scrollDiff
            shouldPreservePrependScrollRef.current = false
            previousScrollHeightRef.current = 0
          }
        })
      }
    }
  }, [fetchedMessages, isSending, setMessages])

  // Reset fetch guard when fetch completes
  useEffect(() => {
    if (!isFetchingNextPage) {
      fetchingMoreRef.current = false
    }
  }, [isFetchingNextPage])

  // Auto-load more messages when at top
  useEffect(() => {
    if (
      isInitialLoadRef.current ||
      !hasNextPage ||
      isFetchingNextPage ||
      fetchingMoreRef.current ||
      !messagesContainerRef.current
    )
      return

    const container = messagesContainerRef.current
    const isAtTop = container.scrollTop < 10
    const contentFitsViewport = container.scrollHeight <= container.clientHeight

    if (isAtTop || contentFitsViewport) {
      fetchingMoreRef.current = true
      shouldPreservePrependScrollRef.current = true
      previousScrollHeightRef.current = container.scrollHeight
      fetchNextPage()
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  // AI-initiated greeting logic
  useEffect(() => {
    if (!chatId || greetingInitiatedRef.current || isSending) return
    if (fetchedMessages === undefined) return

    // Determine whether this user has an existing conversation history.
    // Use localStorage as a secondary signal so a transient empty fetch
    // (network glitch) cannot trick us into showing the first-time greeting
    // to a returning user.
    const hasConversationHistoryKey = `hasConversation_${chatId}`
    const hadPriorConversation = localStorage.getItem(hasConversationHistoryKey) === '1'

    if (fetchedMessages.length > 0) {
      // Persist the fact that this chat has messages so future sessions
      // never accidentally treat the user as brand-new.
      localStorage.setItem(hasConversationHistoryKey, '1')
    }

    const isReturningUser = fetchedMessages.length > 0 || hadPriorConversation

    const shouldGreet = () => {
      if (!isReturningUser) return true

      const lastMessage = fetchedMessages[fetchedMessages.length - 1]
      if (!lastMessage?.created_at) return false

      const lastMessageTime = new Date(lastMessage.created_at).getTime()
      const now = Date.now()
      const hoursSinceLastMessage = (now - lastMessageTime) / (1000 * 60 * 60)

      if (hoursSinceLastMessage >= 12) {
        const lastGreetingKey = `lastGreeting_${chatId}`
        const lastGreetingTime = localStorage.getItem(lastGreetingKey)

        if (!lastGreetingTime) return true

        const hoursSinceLastGreeting = (now - Number.parseInt(lastGreetingTime)) / (1000 * 60 * 60)
        return hoursSinceLastGreeting >= 12
      }

      return false
    }

    if (shouldGreet()) {
      greetingInitiatedRef.current = true

      const lastGreetingKey = `lastGreeting_${chatId}`
      localStorage.setItem(lastGreetingKey, Date.now().toString())

      const firstName = user?.first_name || user?.full_name?.split(' ')[0]
      const welcomeBack = firstName ? `Welcome back, ${firstName}!` : 'Welcome back!'

      const initialGreetings = [
        `${firstName ? `Hello ${firstName},` : 'Hello,'} I'm Pelgo AI.\nI'll be your coach as we navigate this transition together.\n\nHow are you holding up? I know things might feel uncertain right now, but I'm here to help you turn this into a fresh start.\n\nWhen you're ready to take that first step, go ahead and upload your resume. Even if it's outdated, it gives us a foundation to build on. I'll help you refine the details and get you ready for your next great role.\n\nOr if you don't have a resume yet, no worries, just let me know - we can help you with that too.`,
      ]

      const welcomeBackGreetings = [
        `${welcomeBack} How are you doing? Is there anything I can help you with today?`,
        `${welcomeBack} How have things been going? What would you like to talk about?`,
        `${welcomeBack} I hope you're doing well. What's on your mind today?`,
        `${welcomeBack} How are things progressing? Is there anything new you'd like to discuss?`,
      ]

      const selectRandom = (arr: string[]): string => {
        return arr[Math.floor(Math.random() * arr.length)] as string
      }

      // A returning user always gets the welcome-back variant, even when
      // fetchedMessages happens to be empty due to a transient network issue.
      const greetingContent = isReturningUser
        ? selectRandom(welcomeBackGreetings)
        : selectRandom(initialGreetings)

      const quickActions: QuickAction[] = []

      const greetingMessage: ChatMessage = {
        id: `greeting-${Date.now()}`,
        role: 'assistant',
        content: greetingContent,
        created_at: new Date().toISOString(),
        ...(quickActions.length > 0 && { msg_metadata: { quick_actions: quickActions } }),
      }

      setMessages((prev) => [...prev, greetingMessage])

      const saveGreeting = async () => {
        try {
          const token = localStorage.getItem('auth_token')
          const response = await fetch(`${API_URL}/api/v1/chat/${chatId}/messages/greeting`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              role: 'assistant',
              content: greetingContent,
              channel: 'web',
            }),
          })
          if (!response.ok) {
            console.error('[Greeting] Server rejected greeting save:', response.status)
            return
          }
          const saved = await response.json()
          // Replace local greeting with server-persisted version so the
          // next refetch cannot create a duplicate.
          if (saved?.id) {
            setMessages((prev) =>
              prev.map((m) =>
                m.id === greetingMessage.id
                  ? { ...m, id: saved.id, created_at: saved.created_at || m.created_at }
                  : m,
              ),
            )
          }
        } catch (error) {
          console.error('[Greeting] Failed to save greeting to database:', error)
        }
      }
      saveGreeting()

      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
      }, 100)
    }
  }, [chatId, fetchedMessages, isSending, user?.first_name, user?.full_name, setMessages])

  const scrollToBottom = useCallback((behavior: ScrollBehavior = 'smooth') => {
    const container = messagesContainerRef.current
    if (!container) return

    if (behavior === 'smooth') {
      container.scrollTo({ top: container.scrollHeight, behavior: 'smooth' })
      return
    }

    container.scrollTop = container.scrollHeight
  }, [])

  // Auto-scroll once when the first assistant content bubble appears.
  useEffect(() => {
    if (!typingAutoFollowRef.current || firstCoachBubbleAutoScrolledRef.current) return

    const streamingId = streamingIdRef.current
    if (streamingId === undefined) return

    const streamingMessage = messages.find((msg) => msg.id === streamingId)
    if (!streamingMessage || streamingMessage.role !== 'assistant') return
    if (!streamingMessage.isStreaming) return

    if (!streamingMessage.content?.trim()) return

    scrollToBottom('auto')
    firstCoachBubbleAutoScrolledRef.current = true
    typingAutoFollowRef.current = false
  }, [messages, scrollToBottom, streamingIdRef])

  // Auto-scroll during streaming
  useEffect(() => {
    const streamingId = streamingIdRef.current
    if (streamingId === undefined) return

    const streamingMessage = messages.find((msg) => msg.id === streamingId)
    const typingPlaceholderActive = Boolean(
      streamingMessage &&
        streamingMessage.role === 'assistant' &&
        streamingMessage.isStreaming &&
        !streamingMessage.content?.trim(),
    )

    if (typingAutoFollowRef.current && typingPlaceholderActive && !userScrolledUpRef.current) {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
      }
    }
  }, [messages, streamingIdRef, userScrolledUpRef])

  const handleScroll = () => {
    if (messagesContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current
      const nearBottom = scrollHeight - scrollTop - clientHeight < 100
      const nearTop = scrollTop < 100

      // Persist scroll position for tab-switch restoration
      setScrollPosition(scrollTop)

      if (streamingIdRef.current !== undefined && !nearBottom) {
        userScrolledUpRef.current = true
      }

      setShowScrollButton(!nearBottom && messages.length > 0)

      if (nearTop && hasNextPage && !isFetchingNextPage && !fetchingMoreRef.current) {
        fetchingMoreRef.current = true
        shouldPreservePrependScrollRef.current = true
        previousScrollHeightRef.current = messagesContainerRef.current.scrollHeight
        fetchNextPage()
      }
    }
  }

  const messageGroups = useMemo(() => groupMessagesByDate(messages), [messages])
  const handleMatchWithJobs = useCallback(async () => {
    if (isMatchingJobs) return
    const resumeId = userDocumentId ?? currentResume?.id
    if (!resumeId) {
      navigate('/resume')
      return
    }

    setIsMatchingJobs(true)
    try {
      const numericChatId = chatId ? Number(chatId) : null
      await externalJobsService.triggerMatchResume(resumeId, numericChatId, isMobile)
    } catch (error) {
      console.error('Failed to trigger job matching from chat:', error)
    } finally {
      setIsMatchingJobs(false)
    }
  }, [chatId, currentResume, isMatchingJobs, isMobile, navigate, userDocumentId])

  if (chatsLoading || !chatId) {
    return (
      <div className={cn('flex h-full items-center justify-center', className)}>
        <div className="text-center space-y-4">
          <Loader className="h-6 w-6 animate-spin mx-auto text-blue-600" />
          <p className="text-sm text-muted-foreground">Loading...</p>
        </div>
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col h-full', className)}>
      {/* Chat header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b bg-background shrink-0">
        <Sparkles className="h-4 w-4 text-sky-500 shrink-0" />
        <span className="text-sm font-semibold tracking-tight">Pelgo coach</span>
      </div>

      {/* Messages container */}
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className="flex-1 min-h-0 overflow-y-auto px-4 py-4"
      >
        {messages.length > 0 ? (
          <div className="space-y-4">
            {isFetchingNextPage && (
              <div className="flex items-center justify-center py-3">
                <Loader className="h-4 w-4 animate-spin text-blue-600" />
                <span className="ml-2 text-xs text-muted-foreground">
                  Loading older messages...
                </span>
              </div>
            )}
            {hasNextPage && !isFetchingNextPage && (
              <button
                type="button"
                onClick={() => {
                  if (fetchingMoreRef.current) return
                  fetchingMoreRef.current = true
                  shouldPreservePrependScrollRef.current = true
                  if (messagesContainerRef.current) {
                    previousScrollHeightRef.current = messagesContainerRef.current.scrollHeight
                  }
                  fetchNextPage()
                }}
                className="flex items-center justify-center py-2 w-full hover:bg-muted/50 rounded transition-colors"
              >
                <span className="text-xs text-muted-foreground hover:text-foreground">
                  Click or scroll up for older messages
                </span>
              </button>
            )}
            {Object.entries(messageGroups).map(([dateLabel, msgs]) => (
              <div key={dateLabel}>
                <div className="flex items-center gap-3 my-4">
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                  <span className="text-xs font-medium text-black dark:text-white shrink-0">
                    {dateLabel}
                  </span>
                  <div className="flex-1 h-px bg-slate-200 dark:bg-slate-700" />
                </div>

                <div className="space-y-4">
                  {msgs.map((msg) => {
                    const showLegacyBubble =
                      msg.role === 'user' ||
                      msg.error ||
                      msg.msg_metadata?.type === 'resume_upload' ||
                      (msg.isStreaming && !msg.content)
                    return (
                      <div
                        key={msg.id}
                        className={cn(
                          'flex flex-col',
                          msg.role === 'user' ? 'items-end' : 'items-start',
                        )}
                      >
                        <div
                          className={cn(
                            'break-words overflow-x-hidden',
                            showLegacyBubble && 'rounded-2xl px-4 py-2.5 max-w-[90%] shadow-sm',
                            showLegacyBubble &&
                              (msg.role === 'user'
                                ? 'bg-sky-100 text-black border border-sky-200 rounded-br-sm'
                                : msg.error
                                  ? 'bg-red-50 border border-red-200 rounded-bl-sm'
                                  : 'bg-gradient-to-br from-muted to-muted/70 rounded-bl-sm text-black dark:text-white'),
                          )}
                        >
                          {(() => {
                            const MessageRenderer = MESSAGE_RENDERERS[getMessageDisplayType(msg)]
                            return (
                              <MessageRenderer
                                msg={msg}
                                onRevisionGenerated={(revision) => {
                                  setMessages((prev) =>
                                    prev.map((m) => {
                                      if (m.id === msg.id) {
                                        const { resumePreview: _, ...rest } = m
                                        return { ...rest, revision }
                                      }
                                      return m
                                    }),
                                  )
                                }}
                              />
                            )
                          })()}
                        </div>
                        {(() => {
                          const jobContext =
                            msg.msg_metadata?.job_context ||
                            msg.jobContext ||
                            parseJobContext(msg.content)
                          return jobContext ? <InlineJobChip jobContext={jobContext} /> : null
                        })()}
                        {(() => {
                          const matchData =
                            msg.msg_metadata?.match_data ||
                            msg.matchData ||
                            parseMatchData(msg.content)
                          return matchData ? <InlineMatchCard matchData={matchData} /> : null
                        })()}
                        {msg.role === 'assistant' && !msg.error && <MessageFeedback msg={msg} />}
                        {msg.msg_metadata?.quick_actions &&
                          msg.msg_metadata.quick_actions.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-2 px-1">
                              {msg.msg_metadata.quick_actions.map((action) => (
                                <Button
                                  key={action.label}
                                  variant="outline"
                                  size="sm"
                                  className="text-xs rounded-full px-4"
                                  onClick={() => {
                                    if (action.action === 'navigate') {
                                      navigate(action.target)
                                    } else if (action.action === 'support_request') {
                                      setSupportMode(true)
                                    } else if (action.action === 'message') {
                                      sendMessageStreaming(action.target)
                                    }
                                  }}
                                >
                                  {action.label}
                                </Button>
                              ))}
                            </div>
                          )}
                        {msg.msg_metadata?.quality_score &&
                          (!isMobile || msg.role === 'assistant') && (
                            <div className="mt-2 max-w-[90%]">
                              <MobileScoreCard
                                score={msg.msg_metadata.quality_score.overall}
                                breakdown={
                                  isMobile
                                    ? [
                                        {
                                          dimension: 'Skills',
                                          score: msg.msg_metadata.quality_score.skills_coverage,
                                        },
                                        {
                                          dimension: 'Experience',
                                          score: msg.msg_metadata.quality_score.experience_clarity,
                                        },
                                        {
                                          dimension: 'Formatting',
                                          score: msg.msg_metadata.quality_score.formatting,
                                        },
                                      ]
                                    : [
                                        {
                                          dimension: 'Skills Coverage',
                                          score: msg.msg_metadata.quality_score.skills_coverage,
                                        },
                                        {
                                          dimension: 'Experience Clarity',
                                          score: msg.msg_metadata.quality_score.experience_clarity,
                                        },
                                        {
                                          dimension: 'Formatting',
                                          score: msg.msg_metadata.quality_score.formatting,
                                        },
                                      ]
                                }
                                onImprove={() =>
                                  isMobile
                                    ? sendMessageStreaming(
                                        "I'd like to improve my resume. Can you help me with specific suggestions?",
                                      )
                                    : sendMessageStreaming('Improve with Coach')
                                }
                                onMatchJobs={() => {
                                  if (isMobile) {
                                    navigate('/opportunities')
                                    return
                                  }
                                  void handleMatchWithJobs()
                                }}
                              />
                            </div>
                          )}
                        {msg.msg_metadata?.quick_replies &&
                          msg.msg_metadata.quick_replies.length > 0 && (
                            <div className="mt-2 max-w-[90%]">
                              <QuickReplyChips
                                options={msg.msg_metadata.quick_replies}
                                onSelect={(option) => {
                                  setMessages((prev) =>
                                    prev.map((m) =>
                                      m.id === msg.id
                                        ? {
                                            ...m,
                                            msg_metadata: {
                                              ...(m.msg_metadata ?? {}),
                                              quick_replies: [],
                                            },
                                          }
                                        : m,
                                    ),
                                  )
                                  if (option === 'View matched jobs') {
                                    const matchedResumeId =
                                      msg.msg_metadata?.quick_replies_document_id
                                    if (
                                      typeof matchedResumeId === 'number' &&
                                      matchedResumeId > 0
                                    ) {
                                      navigate(`/opportunities?resume_id=${matchedResumeId}`)
                                    } else {
                                      navigate('/opportunities')
                                    }
                                  } else {
                                    sendMessageStreaming(option)
                                  }
                                }}
                              />
                            </div>
                          )}
                        <span className="text-xs text-muted-foreground mt-1 px-2">
                          {formatMessageTime(msg.created_at || new Date())}
                        </span>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
            {messages.length >= 6 && <ConversationFeedback chatId={chatId} />}
            {activeStatusText && (
              <div className="flex items-center gap-2 py-2 px-1">
                <Loader className="h-3 w-3 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground italic">{activeStatusText}</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            <div className="text-center space-y-3 max-w-[250px]">
              <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-blue-500 to-blue-500 flex items-center justify-center mb-3 shadow-lg shadow-blue-500/30">
                <MessageCircle className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-base font-medium">Start the Conversation</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Share what's on your mind -- job search, interview prep, or just processing your
                feelings.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          type="button"
          onClick={() => scrollToBottom('smooth')}
          className="absolute bottom-20 right-4 p-2 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all z-10"
          aria-label="Scroll to bottom"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      )}

      {/* Input area */}
      <ChatInput
        onSendMessage={sendMessageStreaming}
        onMatchComplete={async (userMsg, assistantMsg) => {
          setMessages((prev) => [...prev, userMsg, assistantMsg])

          if (chatId) {
            const token = localStorage.getItem('auth_token')
            try {
              await fetch(`${API_URL}/api/v1/chat/${chatId}/messages/greeting`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  role: 'user',
                  content: userMsg.content,
                  channel: 'web',
                  msg_metadata: userMsg.msg_metadata,
                }),
              })
              await fetch(`${API_URL}/api/v1/chat/${chatId}/messages/greeting`, {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  role: 'assistant',
                  content: assistantMsg.content,
                  channel: 'web',
                  msg_metadata: assistantMsg.msg_metadata,
                }),
              })
            } catch (error) {
              console.error('Failed to persist match messages:', error)
            }
          }
        }}
        onDocumentReady={(documentId) => {
          setUserDocumentId(documentId)
          if (chatId) {
            queryClient.invalidateQueries({ queryKey: ['chats', chatId, 'messages', 'infinite'] })
          }
          void refetchMessages()
        }}
        isSending={isSending}
        supportMode={supportMode}
        onSupportModeChange={setSupportMode}
        userEmail={user?.email}
        userName={user?.full_name || user?.first_name}
        onSupportSubmitted={(supportMessage) => {
          const confirmationMsg: ChatMessage = {
            id: `support-${Date.now()}`,
            role: 'assistant',
            content: `Thanks for reaching out. All messages are answered within hours. We'll follow up at ${user?.email || 'your email'}.`,
            created_at: new Date().toISOString(),
          }
          setMessages((prev) => [
            ...prev,
            {
              id: `support-user-${Date.now()}`,
              role: 'user',
              content: supportMessage,
              created_at: new Date().toISOString(),
            },
            confirmationMsg,
          ])
        }}
        compact
      />
      <p className="text-[10px] text-center text-muted-foreground pb-2">
        Pelgo can make mistakes. Check important info.
      </p>
    </div>
  )
}
